import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrgScopeStore } from './org-scope-store';
import { ConsoleCaptureService } from '../ticketing/services/console-capture.service';

/**
 * Sends the errors this page runs into to BC_Corporate (`POST /client-logs`), which writes them to
 * Application Insights beside the server's own.
 *
 * Before this, a save that failed was known only to whoever read the snackbar, and one that never
 * left the browser was known to nobody -- when a user reported "I added 20 steps and 3 were saved",
 * there was nothing to look at. Now a failed save and an uncaught error in the page both leave a
 * record: which page, which call, what status, what the user was told.
 *
 * Request bodies are never sent -- they carry client data. Query strings are dropped from URLs for
 * the same reason.
 */

/** Set on the report call itself, so a report that fails is not reported in turn. */
export const SKIP_ERROR_REPORT = new HttpContextToken<boolean>(() => false);

const MAX_REPORTS_PER_MINUTE = 20;
const DUPLICATE_WINDOW_MS = 60_000;
const MAX_DETAIL = 4000;

export interface ClientErrorReport {
  kind: 'http' | 'script';
  message: string;
  method?: string;
  requestUrl?: string;
  status?: number;
  detail?: string;
}

@Injectable({ providedIn: 'root' })
export class ClientErrorReporter {
  // Resolved on first use: HttpClient is built from the interceptor chain, and the interceptor
  // below needs this service, so asking for it up front would be a circular dependency.
  private readonly injector = inject(Injector);
  private readonly url = `${environment.apiConfig.corporateAPI.endpoint}/client-logs`;

  private readonly recent = new Map<string, number>();
  private sentThisMinute = 0;
  private minuteStartedAt = Date.now();

  report(report: ClientErrorReport): void {
    try {
      if (!this.shouldSend(report)) return;

      const body = {
        ...report,
        message: report.message.slice(0, 2000),
        requestUrl: withoutQuery(report.requestUrl),
        detail: report.detail?.slice(0, MAX_DETAIL),
        pageUrl: location.pathname,
        companyId: Number(OrgScopeStore.getItem('managedCompanyId')) || null
      };

      this.injector.get(HttpClient)
        .post(this.url, body, { context: new HttpContext().set(SKIP_ERROR_REPORT, true) })
        .subscribe({ error: () => { /* Reporting must never become a second failure. */ } });
    } catch {
      // Same: whatever goes wrong here, the page carries on.
    }
  }

  /** Drops repeats of the same error and caps the rate, so a loop cannot flood the logs. */
  private shouldSend(report: ClientErrorReport): boolean {
    const now = Date.now();
    if (now - this.minuteStartedAt > 60_000) {
      this.minuteStartedAt = now;
      this.sentThisMinute = 0;
    }
    if (this.sentThisMinute >= MAX_REPORTS_PER_MINUTE) return false;

    const key = `${report.kind}|${report.method ?? ''}|${withoutQuery(report.requestUrl) ?? ''}|${report.status ?? ''}|${report.message}`;
    const last = this.recent.get(key);
    if (last !== undefined && now - last < DUPLICATE_WINDOW_MS) return false;

    this.recent.set(key, now);
    if (this.recent.size > 200) this.recent.clear();
    this.sentThisMinute++;
    return true;
  }
}

/**
 * Reports every failed write -- POST, PUT, PATCH, DELETE -- to any API. Reads are left out: a failed
 * GET is usually a refresh away from fine, and they are the bulk of the traffic. The error is passed
 * on untouched, so each screen still handles it as it always did.
 */
export const clientErrorInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_ERROR_REPORT) || ['GET', 'HEAD', 'OPTIONS'].includes(req.method)) {
    return next(req);
  }
  const reporter = inject(ClientErrorReporter);
  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        reporter.report({
          kind: 'http',
          message: error.status === 0 ? 'Save did not reach the server (network or CORS)' : 'Save failed',
          method: req.method,
          requestUrl: req.url,
          status: error.status,
          detail: describeBody(error.error)
        });
      }
      return throwError(() => error);
    })
  );
};

/**
 * Angular's last stop for an error nothing else caught. Still logs to the console as the default
 * handler does, and now also reports it.
 */
@Injectable()
export class ReportingErrorHandler extends ErrorHandler {
  private readonly injector = inject(Injector);

  override handleError(error: unknown): void {
    super.handleError(error);

    // Also kept in a small in-memory ring buffer, so a user reporting a bug can attach the last few
    // errors to the ticket. Resolved lazily for the same circular-dependency reason as the reporter.
    try {
      this.injector.get(ConsoleCaptureService).recordError(error);
    } catch {
      // Capture is an aid to bug reports; it must never be why an error goes unreported.
    }

    // An HTTP failure that reached here was a read (writes are reported by the interceptor).
    if (error instanceof HttpErrorResponse) return;

    const e = (error as any)?.rejection ?? error;
    this.injector.get(ClientErrorReporter).report({
      kind: 'script',
      message: String(e?.message ?? e ?? 'Unknown error'),
      detail: typeof e?.stack === 'string' ? e.stack : undefined
    });
  }
}

function withoutQuery(url: string | undefined): string | undefined {
  return url?.split('?')[0];
}

function describeBody(body: unknown): string | undefined {
  if (body == null) return undefined;
  if (typeof body === 'string') return body;
  try {
    return JSON.stringify(body);
  } catch {
    return undefined;
  }
}
